import { useEffect, useState } from 'react';
import { BellRing, X } from 'lucide-react';
import { useLocation } from 'wouter';

type PushBannerPayload = {
  title?: string;
  body?: string;
  deepLink?: string;
  url?: string;
  kind?: string;
  critical?: boolean;
  deliveryId?: number;
  roomId?: number;
  roomTitle?: string;
  roomHeadcount?: number;
  data?: Record<string, unknown>;
};

type PushBannerMessage = { type?: string; payload?: PushBannerPayload };

function currentReturnPath() {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  url.searchParams.delete('room');
  url.searchParams.delete('drawer');
  url.searchParams.delete('returnTo');
  const target = `${url.pathname}${url.search}${url.hash}`;
  if (!target || target === '/' || target.startsWith('/login')) return null;
  return target;
}

function roomLinkWithReturnTo(rawLink: string, roomId?: number) {
  if (typeof window === 'undefined') return rawLink;
  try {
    const url = new URL(rawLink, window.location.origin);
    const isRoomLink = (Number.isSafeInteger(Number(roomId)) && Number(roomId) > 0) || url.searchParams.has('room');
    const returnTo = isRoomLink ? currentReturnPath() : null;
    if (returnTo && !url.searchParams.has('returnTo')) url.searchParams.set('returnTo', returnTo);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return rawLink;
  }
}

function payloadDeliveryId(payload: PushBannerPayload) {
  const id = Number(payload.deliveryId ?? payload.data?.deliveryId ?? 0);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

function isCriticalPayload(payload: PushBannerPayload) {
  const kind = String(payload.kind ?? payload.data?.kind ?? '');
  return Boolean(
    payload.critical
    || payload.data?.critical
    || kind.includes('meeting_reconciliation')
    || kind.includes('one_hour')
    || kind.includes('due'),
  );
}

function recordDeliveryEvent(payload: PushBannerPayload, event: 'clicked' | 'acknowledged') {
  const deliveryId = payloadDeliveryId(payload);
  if (!deliveryId) return;
  void fetch('/api/exai/operational-briefing/push/delivery-events', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ deliveryId, event }),
  }).catch(() => undefined);
}

export function MartinaPushBanner() {
  const [, navigate] = useLocation();
  const [payload, setPayload] = useState<PushBannerPayload | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handleMessage = (event: MessageEvent<PushBannerMessage>) => {
      if (event.data?.type !== 'MARTINA_PUSH' || !event.data.payload) return;
      setPayload(event.data.payload);
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, []);

  if (!payload) return null;
  const rawDeepLink = payload.deepLink || payload.url || (payload.roomId ? `/?room=${payload.roomId}&drawer=1` : '/');
  const roomParam = (() => {
    try {
      return new URL(rawDeepLink, window.location.origin).searchParams.get('room');
    } catch {
      return null;
    }
  })();
  const isRoomPush = (Number.isSafeInteger(Number(payload.roomId)) && Number(payload.roomId) > 0)
    || (Number.isSafeInteger(Number(roomParam)) && Number(roomParam) > 0);
  const deepLink = roomLinkWithReturnTo(rawDeepLink, payload.roomId);
  const roomTitle = payload.roomTitle || (isRoomPush ? 'Stanza di Martina' : 'Aggiornamento email');
  const critical = isCriticalPayload(payload);
  // Bug reale mobile (25 lug 2026): il banner appariva "piu' largo dello
  // schermo" con testo tagliato al bordo destro. inset-x da solo dipende dal
  // containing block piu' vicino con transform/will-change (comune nei
  // modali) e puo' non coincidere col vero viewport. max-w in unita' 100vw
  // e' sempre relativo al viewport reale, indipendentemente da dove il
  // banner finisce annidato: tetto di sicurezza che non puo' mai sforare.
  const bannerClass = critical
    ? 'fixed inset-x-4 bottom-16 z-[70] max-w-[calc(100vw-2rem)] border-2 border-destructive bg-destructive/10 p-4 shadow-2xl ring-2 ring-destructive/30 animate-pulse md:inset-x-auto md:right-5 md:w-[min(29rem,calc(100vw-2rem))]'
    : 'fixed inset-x-4 bottom-20 z-[60] max-w-[calc(100vw-2rem)] border border-border bg-background p-4 shadow-lg md:inset-x-auto md:right-5 md:w-[min(26rem,calc(100vw-2.5rem))]';

  return (
    <aside
      className={`${bannerClass} pointer-events-auto isolate`}
      role={critical ? 'alert' : 'status'}
      aria-live={critical ? 'assertive' : 'polite'}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-start gap-3">
        <BellRing className={critical ? 'mt-0.5 h-5 w-5 shrink-0 text-destructive' : 'mt-0.5 h-5 w-5 shrink-0 text-primary'} aria-hidden="true" />
        <div className="min-w-0 flex-1 break-words">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Martina · {critical ? 'Critica · ' : ''}{roomTitle}
          </p>
          <p className="mt-1 text-sm font-semibold text-foreground">{payload.title || 'Aggiornamento operativo'}</p>
          <p className="mt-1 text-sm text-muted-foreground">{payload.body || 'Hai un aggiornamento da decidere.'}</p>
          {payload.roomHeadcount && payload.roomHeadcount > 1 && (
            <p className="mt-2 text-xs text-muted-foreground">{payload.roomHeadcount} elementi nella stessa Stanza</p>
          )}
          <button type="button" className={critical ? 'mt-3 border border-destructive bg-destructive px-3 py-2 text-sm font-semibold text-destructive-foreground' : 'mt-3 border border-primary bg-primary px-3 py-2 text-sm font-medium text-primary-foreground'} onClick={() => { recordDeliveryEvent(payload, 'clicked'); setPayload(null); navigate(deepLink); }}>
            {isRoomPush ? (critical ? 'Apri Stanza critica' : 'Apri Stanza') : 'Apri email'}
          </button>
        </div>
        {/* Ogni banner deve poter essere chiuso, anche se critico: chiudere la
            proiezione non risolve né archivia la fonte. Stoppa il click in
            cattura per evitare il click-through sul form sotto il banner. */}
        <button
          type="button"
          className="-m-2 flex h-11 w-11 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
          aria-label="Chiudi avviso Martina"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => { event.preventDefault(); event.stopPropagation(); recordDeliveryEvent(payload, 'acknowledged'); setPayload(null); }}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </aside>
  );
}
